import { getOwner } from "@ember/application"
import { registerDestructor } from "@ember/destroyable"
import type { NotifierFunction } from "@shared/components/providers/notifier-context"
import { NotifierProvider } from "@shared/components/providers/NotifierProvider"
import { SupergrainProvider } from "@shared/components/providers/SupergrainProvider"
import { handleError } from "cc-frontend/error-handling"
import type NotifierService from "cc-frontend/services/notifier"
import type ReactStore from "cc-frontend/services/react-store"
import Modifier from "ember-modifier"
import type { ErrorInfo, FunctionComponent, ReactNode } from "react"
import { Component, createElement } from "react"
import { EmberServiceProvider } from "react-app/providers/EmberServiceProvider"
import { flushSync } from "react-dom"
import type { Root } from "react-dom/client"
import { createRoot } from "react-dom/client"

// Dev-only: enables Option+Right-Click on any rendered React element to open its source in your
// editor (via vite-plugin-react-click-to-component). Embroider owns index.html, so the plugin's
// automatic <script> injection doesn't run — importing the client here is the documented fallback.
if (import.meta.env.DEV) {
  import("vite-plugin-react-click-to-component/client")
}

// Based on https://github.com/ef4/ember-react-example

// Define an ErrorBoundary to catch errors in React components.
// See https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
class ErrorBoundary extends Component<{ children: ReactNode }> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    handleError(error)
  }

  render() {
    if (this.state.hasError) {
      return createElement("div", null, "Something went wrong.")
    }
    return this.props.children
  }
}

interface ReactRenderSig {
  Args: {
    Positional: [FunctionComponent]
    Named: React.Attributes
  }
}

/**
 * Usage:
 *
 * 1. In the parent Ember component's ts file, add the top-level React component (function) as a property:
 *    ```ts
 *    import MyTopReact from "cc-frontend/react-components/my-to-react"
 *
 *    export default class MyEmberComponent extends Component<Sig> {
 *      readonly MyTopReact = MyTopReact
 *    }
 *    ```
 *    The React component can either be imported from a library or defined in our .tsx file.
 * 2. In the parent Ember component's template, add
 *    ```hbs
 *    <div {{react-render this.MyTopReact foo=@foo bar=@bar}}></div>
 *    ```
 *    at the desired render location. The arguments after the React component are the React props;
 * note that the prop names *don't* start with `@` (however, the values are ordinary Ember S-expressions).
 *
 * Inside the rendered component, you have access to these provider hooks:
 * 1. useEmberService(name: string) - looks up an Ember service by name.
 */
export default class ReactRenderModifier extends Modifier<ReactRenderSig> {
  private root: Root | null = null

  modify(element: Element, [reactComponent]: [FunctionComponent], props: React.Attributes) {
    // @service does not work in tsx files with our build pipeline. Use owner.lookup instead.
    const owner = getOwner(this)!
    const reactStore = owner.lookup("service:react-store") as ReactStore
    const notifierService = owner.lookup("service:notifier") as NotifierService

    // Pipe React notifications to the Ember Notifier service (its "error" level is `danger`).
    // The service requires body text, so a headline-only notification shows as the message.
    const notifier: NotifierFunction = (type, title, message) => {
      const level = type === "error" ? "danger" : type
      notifierService[level](message ?? title, message === undefined ? {} : { title })
    }

    if (!this.root) {
      this.root = createRoot(element)
      registerDestructor(this, () => {
        this.root?.unmount()
        this.root = null
      })
    }
    // flushSync, because every island is its own React root. `root.render` only *queues* work,
    // and React drains roots one at a time in separate scheduler tasks — so the browser paints
    // between them and a planner reveals its lessons one by one over hundreds of milliseconds.
    // Rendering inside Ember's own render pass puts everything Ember is mounting right now into
    // a single paint. Anything mounted later still arrives on its own, asynchronously, as before.
    flushSync(() => {
      this.root!.render(
        <ErrorBoundary>
          <EmberServiceProvider modifier={this}>
            <NotifierProvider notifier={notifier}>
              <SupergrainProvider store={reactStore.documentStore}>
                {createElement(reactComponent, { ...props })}
              </SupergrainProvider>
            </NotifierProvider>
          </EmberServiceProvider>
        </ErrorBoundary>
      )
    })
  }
}
